import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { WebPageService } from '../../Services/webpage.service';

interface TimelineItem {
  year: string;
  short: string;
  title: string;
  body: string;
  tag: string;
  placeholder: string;
  tone: 'sepia' | 'sepia-warm' | 'warm' | 'modern';
  /** Real archive image coming from the API; falls back to the decorative placeholder when absent */
  image?: string;
}

interface MapNode {
  id: string;
  label: string;
  sub?: string;
  x: number;
  y: number;
  big?: boolean;
  self?: boolean;
}

interface ScopeStat {
  n: string;
  l: string;
  accent: 'brick' | 'navy' | 'teal';
}

interface Scope {
  id: 'global' | 'ar' | 'sf';
  eyebrow: string;
  title: string;
  body: string;
  mapShape: 'world' | 'argentina' | 'city';
  mapMeta: string;
  stats: ScopeStat[];
  nodes: MapNode[];
}

/** One tab + detail panel of the "Presencia" section (similar_institutions.items) */
interface SimilarItem {
  item: string;
  subitem: string;
  title: string;
  description: string;
  image?: string;
  order?: number;
}

/** Stat card at the bottom of the "Presencia" section (similar_institutions.cards) */
interface SimilarCard {
  title: string;
  subtitle: string;
}

@Component({
  selector: 'app-history-presence',
  templateUrl: './HistoryPresence.component.html',
  styleUrls: ['./HistoryPresence.component.scss']
})
export class HistoryPresenceComponent implements OnInit {
  @ViewChild('rail') rail!: ElementRef<HTMLElement>;

  /** Tones cycled through API milestones, oldest → newest, to keep the archive-photo look */
  private readonly milestoneTones: TimelineItem['tone'][] = ['sepia', 'sepia-warm', 'warm', 'modern'];

  // ===== Historia: interactive timeline =====
  // Default content used as a fallback until/if the API responds.
  timeline: TimelineItem[] = [
    {
      year: '1856',
      short: 'Fundación de la Congregación',
      title: 'Nacen las Adoratrices',
      body: 'Santa María Micaela funda en Madrid la Congregación de las Adoratrices Esclavas del Santísimo Sacramento y de la Caridad. Una misión de fe y entrega que viajará al mundo.',
      tag: 'Origen',
      placeholder: 'foto archivo · fundación 1856',
      tone: 'sepia'
    },
    {
      year: '1928',
      short: 'Llegada a Santa Fe',
      title: 'Las hermanas llegan al Litoral',
      body: 'La Congregación se establece en Santa Fe, dando los primeros pasos de una obra educativa que acompañaría a generaciones de familias santafesinas.',
      tag: 'Hito',
      placeholder: 'archivo · primer convento',
      tone: 'sepia'
    },
    {
      year: '1942',
      short: 'Apertura del Instituto',
      title: 'Se inaugura el Instituto San José',
      body: 'Se inauguran las primeras aulas del Instituto. Un puñado de alumnas componen la primera promoción, junto con el equipo fundador de docentes y religiosas.',
      tag: 'Educación',
      placeholder: 'foto en blanco y negro · aula 1942',
      tone: 'sepia'
    },
    {
      year: '1968',
      short: 'Crece el Nivel Primario',
      title: 'Ampliación del edificio histórico',
      body: 'Se inauguran nuevas aulas, capilla y patios. El crecimiento sostenido consolida al Instituto como referente educativo en la ciudad.',
      tag: 'Crecimiento',
      placeholder: 'archivo · ampliación edificio',
      tone: 'sepia-warm'
    },
    {
      year: '1985',
      short: 'Apertura del Nivel Secundario',
      title: 'Una propuesta integral',
      body: 'Comienza el Nivel Secundario, completando una trayectoria educativa que acompaña a los estudiantes desde el jardín hasta el egreso.',
      tag: 'Niveles',
      placeholder: 'archivo · primera promoción secundaria',
      tone: 'warm'
    },
    {
      year: '2008',
      short: 'Inauguración del nuevo edificio',
      title: 'Modernización de los espacios',
      body: 'Se inaugura un nuevo edificio con laboratorios, sala de informática, gimnasio y espacios para talleres extracurriculares.',
      tag: 'Infraestructura',
      placeholder: 'foto · edificio nuevo 2008',
      tone: 'modern'
    },
    {
      year: '2020',
      short: 'Educación en pandemia',
      title: 'Nos reinventamos',
      body: 'En tiempos de pandemia, toda la comunidad educativa se adapta con propuestas virtuales, sosteniendo el vínculo y los aprendizajes.',
      tag: 'Comunidad',
      placeholder: 'foto · clases virtuales',
      tone: 'modern'
    },
    {
      year: '2026',
      short: 'Hoy: Formando Corazones',
      title: '84 años abriendo camino',
      body: 'Con más de 1.200 estudiantes, cuatro niveles educativos y una comunidad viva, el Instituto sigue formando corazones y construyendo futuros.',
      tag: 'Hoy',
      placeholder: 'foto actual · alumnos 2026',
      tone: 'modern'
    }
  ];

  readonly historiaStats = [
    { n: '170', l: 'años de la Congregación' },
    { n: '84', l: 'años en Santa Fe' },
    { n: '1.200+', l: 'estudiantes hoy' },
    { n: '4', l: 'niveles educativos' }
  ];

  /** Tonal palettes for the decorative archive-photo placeholder, keyed by epoch */
  private readonly placeholderPalettes: { [k: string]: { bg: string; stripe: string; ink: string } } = {
    sepia: { bg: '#e8d8c4', stripe: '#d4bfa3', ink: '#6b4f33' },
    'sepia-warm': { bg: '#e6cdb8', stripe: '#cfae90', ink: '#684832' },
    warm: { bg: '#e6d4c8', stripe: '#cdb4a3', ink: '#5a3f2e' },
    modern: { bg: '#dfe6ea', stripe: '#c8d3d9', ink: '#3d4d57' }
  };

  // Default to the first (oldest) milestone so the timeline opens on the origin.
  activeIndex = 0;

  constructor(private webPageService: WebPageService) {}

  ngOnInit(): void {
    this.loadWebPage();
  }

  /** Fetch institutional web-page content and feed the timeline with API milestones */
  private async loadWebPage(): Promise<void> {
    const res = await this.webPageService.getWebPage();
    const data = res?.data;

    const milestones = data?.milestones;
    if (Array.isArray(milestones) && milestones.length) {
      this.timeline = milestones.map((m, i) => this.toTimelineItem(m, i));
      this.activeIndex = 0;
    }

    this.applySimilarInstitutions(data?.similar_institutions);
  }

  /** Map similar_institutions onto the whole "Presencia" section */
  private applySimilarInstitutions(similar: any): void {
    if (!similar) {
      return;
    }
    this.presenciaTitle = similar.title || '';
    this.presenciaSubtitle = this.stripHtml(similar.subtitle);
    this.presenciaCards = Array.isArray(similar.cards) ? similar.cards : [];

    const items: SimilarItem[] = Array.isArray(similar.items) ? [...similar.items] : [];
    items.forEach(it => { it.title = this.stripHtml(it.title); });
    items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    this.presenciaItems = items;
    this.activeItemIndex = 0;
  }

  /** Map an API milestone ({ year, title, description, image }) to a TimelineItem */
  private toTimelineItem(m: any, index: number): TimelineItem {
    const tone = this.milestoneTones[Math.min(index, this.milestoneTones.length - 1)];
    return {
      year: String(m?.year ?? ''),
      short: m?.title ?? '',
      title: m?.title ?? '',
      body: m?.description ?? '',
      tag: 'Hito',
      placeholder: m?.title ?? 'Archivo',
      tone,
      image: m?.image || undefined
    };
  }

  get historiaItem(): TimelineItem {
    return this.timeline[this.activeIndex];
  }

  goTo(index: number): void {
    this.activeIndex = Math.min(Math.max(index, 0), this.timeline.length - 1);
    this.scrollActiveChipIntoView();
  }

  onRailKey(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.activeIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.activeIndex - 1);
    }
  }

  private scrollActiveChipIntoView(): void {
    setTimeout(() => {
      if (!this.rail) {
        return;
      }
      const railEl = this.rail.nativeElement;
      const chip = railEl.querySelector<HTMLElement>(`[data-idx="${this.activeIndex}"]`);
      if (!chip) {
        return;
      }
      // Scroll ONLY the rail to center the active chip. `scrollIntoView` would
      // also scroll the page/body horizontally, making the whole layout slide.
      const target = chip.offsetLeft - (railEl.clientWidth - chip.offsetWidth) / 2;
      railEl.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    });
  }

  placeholderStyle(tone: string): { [k: string]: string } {
    const p = this.placeholderPalettes[tone] || this.placeholderPalettes['modern'];
    return {
      background: p.bg,
      'background-image': `repeating-linear-gradient(135deg, ${p.bg} 0 14px, ${p.stripe} 14px 15px)`,
      'border-color': p.stripe
    };
  }

  placeholderInk(tone: string): string {
    return (this.placeholderPalettes[tone] || this.placeholderPalettes['modern']).ink;
  }

  chipDotColor(index: number): string {
    if (index === this.activeIndex) {
      return '#fff';
    }
    return index < this.activeIndex ? '#9c2a2a' : '#d8c8b9';
  }

  pad2(n: number): string {
    return String(n).padStart(2, '0');
  }

  // ===== Presencia: scope tabs + schematic map =====
  readonly scopes: Scope[] = [
    {
      id: 'global',
      eyebrow: 'Presencia internacional',
      title: 'Una red que abraza el mundo',
      body: 'Las Adoratrices Esclavas del Santísimo Sacramento están presentes en cuatro continentes. Cuando un alumno se forma en el Instituto, se suma a una comunidad mucho más grande que sus aulas.',
      mapShape: 'world',
      mapMeta: 'Mapa esquemático · no a escala',
      stats: [
        { n: '20+', l: 'países', accent: 'brick' },
        { n: '4', l: 'continentes', accent: 'navy' },
        { n: '170', l: 'años de misión', accent: 'teal' },
        { n: '300+', l: 'comunidades', accent: 'navy' }
      ],
      nodes: [
        { id: 'es', label: 'España', sub: 'Casa fundacional · Madrid', x: 49, y: 38, big: true },
        { id: 'it', label: 'Italia', sub: 'Roma', x: 53, y: 41 },
        { id: 'fr', label: 'Francia', x: 50, y: 36 },
        { id: 'pt', label: 'Portugal', x: 46, y: 40 },
        { id: 'mx', label: 'México', x: 21, y: 50 },
        { id: 'ar', label: 'Argentina', sub: 'Santa Fe · sede local', x: 32, y: 78, big: true, self: true },
        { id: 'br', label: 'Brasil', x: 36, y: 68 },
        { id: 'co', label: 'Colombia', x: 27, y: 60 },
        { id: 'pe', label: 'Perú', x: 25, y: 67 },
        { id: 'ph', label: 'Filipinas', x: 82, y: 55 },
        { id: 'in', label: 'India', x: 71, y: 52 },
        { id: 'jp', label: 'Japón', x: 86, y: 44 },
        { id: 'cd', label: 'R. D. Congo', x: 55, y: 64 },
        { id: 'ao', label: 'Angola', x: 53, y: 71 }
      ]
    },
    {
      id: 'ar',
      eyebrow: 'Presencia en Argentina',
      title: 'De norte a sur del país',
      body: 'En Argentina, las Adoratrices sostienen obras educativas, sociales y pastorales en distintas provincias. Cada casa es un nodo de la misma misión.',
      mapShape: 'argentina',
      mapMeta: 'República Argentina',
      stats: [
        { n: '8', l: 'provincias', accent: 'brick' },
        { n: '14', l: 'casas y obras', accent: 'navy' },
        { n: '6', l: 'colegios', accent: 'teal' },
        { n: '95+', l: 'años en el país', accent: 'navy' }
      ],
      nodes: [
        { id: 'caba', label: 'Buenos Aires', sub: 'CABA', x: 56, y: 70 },
        { id: 'lp', label: 'La Plata', x: 58, y: 73 },
        { id: 'sf', label: 'Santa Fe', sub: 'Sede del Instituto', x: 50, y: 56, big: true, self: true },
        { id: 'cba', label: 'Córdoba', x: 44, y: 60 },
        { id: 'mza', label: 'Mendoza', x: 30, y: 65 },
        { id: 'sj', label: 'San Juan', x: 30, y: 58 },
        { id: 'tuc', label: 'Tucumán', x: 40, y: 38 },
        { id: 'salta', label: 'Salta', x: 38, y: 28 },
        { id: 'mis', label: 'Misiones', x: 70, y: 38 }
      ]
    },
    {
      id: 'sf',
      eyebrow: 'Presencia en Santa Fe',
      title: 'Cerca, en el corazón de la ciudad',
      body: 'En la ciudad de Santa Fe, el Instituto articula con parroquias, comedores y obras pastorales de la Congregación. Una red barrial que sostiene a las familias.',
      mapShape: 'city',
      mapMeta: 'Ciudad de Santa Fe',
      stats: [
        { n: '1', l: 'instituto educativo', accent: 'brick' },
        { n: '3', l: 'comunidades religiosas', accent: 'navy' },
        { n: '5', l: 'obras sociales aliadas', accent: 'teal' },
        { n: '1.200+', l: 'familias en red', accent: 'navy' }
      ],
      nodes: [
        { id: 'inst', label: 'Instituto San José', sub: 'Blvd. Gálvez 1978', x: 48, y: 50, big: true, self: true },
        { id: 'parr', label: 'Parroquia Adoratrices', x: 52, y: 46 },
        { id: 'com1', label: 'Comedor barrio Centenario', x: 38, y: 62 },
        { id: 'com2', label: 'Hogar Sta. Micaela', x: 60, y: 60 },
        { id: 'cap', label: 'Capilla del Instituto', x: 47, y: 53 },
        { id: 'past', label: 'Pastoral juvenil', x: 56, y: 38 }
      ]
    }
  ];

  readonly scopeTabs = [
    { id: 'global', label: 'Global', sub: '20+ países' },
    { id: 'ar', label: 'Argentina', sub: '8 provincias' },
    { id: 'sf', label: 'Santa Fe', sub: 'Red local' }
  ];

  // ===== Presencia content from GET /public/web-page (similar_institutions) =====
  presenciaTitle = '';
  presenciaSubtitle = '';
  presenciaItems: SimilarItem[] = [];
  presenciaCards: SimilarCard[] = [];
  activeItemIndex = 0;

  /** Strip HTML tags from a rich-text value so it renders as plain text */
  private stripHtml(value: string | null | undefined): string {
    return value ? value.replace(/<[^>]*>/g, '').trim() : '';
  }

  get activeItem(): SimilarItem | null {
    return this.presenciaItems[this.activeItemIndex] || null;
  }

  setItem(index: number): void {
    this.activeItemIndex = index;
  }

  scopeId: 'global' | 'ar' | 'sf' = 'global';
  hovered: string | null = null;
  selected: string | null = null;

  get scope(): Scope {
    return this.scopes.find(s => s.id === this.scopeId) as Scope;
  }

  get activeId(): string | null {
    return this.hovered || this.selected;
  }

  get activeNode(): MapNode {
    const nodes = this.scope.nodes;
    return nodes.find(n => n.id === this.activeId) || nodes.find(n => n.self) || nodes[0];
  }

  get selfNode(): MapNode | undefined {
    return this.scope.nodes.find(n => n.self);
  }

  get connectableNodes(): MapNode[] {
    return this.scope.nodes.filter(n => !n.self);
  }

  get detailLabel(): string {
    if (this.hovered) {
      return 'Pasando por';
    }
    return this.selected ? 'Seleccionado' : 'Foco actual';
  }

  setScope(id: 'global' | 'ar' | 'sf'): void {
    this.scopeId = id;
    this.selected = null;
    this.hovered = null;
  }

  onNodeClick(id: string): void {
    this.selected = id === this.selected ? null : id;
  }

  lineOpacity(node: MapNode): number {
    const self = this.selfNode;
    if (this.activeId && self && (this.activeId === node.id || this.activeId === self.id)) {
      return 0.7;
    }
    return 0.25;
  }

  /** Inline style for a map node — position, size and colour depend on state */
  nodeStyle(node: MapNode): { [k: string]: string } {
    const base = node.big ? 14 : 10;
    const size = base + (this.activeId === node.id ? 6 : 0);
    const isActive = this.activeId === node.id;
    const ring = node.self ? 'rgba(156,42,42,0.18)' : 'rgba(29,27,84,0.18)';
    return {
      left: `${node.x}%`,
      top: `${node.y}%`,
      width: `${size}px`,
      height: `${size}px`,
      background: node.self ? 'var(--hp-brick)' : 'var(--hp-navy)',
      'box-shadow': isActive
        ? `0 0 0 6px ${ring}, 0 6px 14px -4px rgba(0,0,0,0.25)`
        : '0 4px 10px -3px rgba(0,0,0,0.25)',
      'z-index': isActive ? '10' : (node.self ? '6' : '5')
    };
  }
}
